import hydraulicDataConfig from '@/api/config/hydraulicDataConfig'
import type { HydraulicType } from '@/types/hydraulic'
import { isDraftId } from './tree-data'
import type { DraftPipe } from './types'

export const sleep = (ms: number) =>
  new Promise((resolve) => setTimeout(resolve, ms))

/**
 * The id of the pipe a `POST /api/pipe` just created, when the response names
 * it.
 *
 * Only an `id` FIELD is trusted. The endpoint's response body is undocumented
 * and nothing else in this codebase reads a create response, so the shapes this
 * API uses elsewhere are probed rather than assumed — but a bare numeric body
 * is deliberately ignored, because a status or a row count would be
 * indistinguishable from an id and hanging sub-pipes off the wrong parent is
 * far worse than the extra list read `findCreatedPipeId` costs.
 *
 * Note `axiosClient` does not unwrap `response.data` — only the separate
 * `createAxiosInstance` installs that interceptor — so what arrives here is the
 * axios response and the payload is one level down.
 */
export const newPipeIdFromResponse = (response: any): number | null => {
  for (const candidate of [
    response?.data?.data?.id,
    response?.data?.id,
    response?.id,
  ]) {
    const id = Number(candidate)
    if (Number.isFinite(id) && id > 0) return id
  }
  return null
}

/**
 * The pipe a create made, picked out of a list read: same parent, same
 * description, and not one of `excludeIds` — the pipes already known before
 * the create, so an older pipe that happens to share the name is never taken
 * for the new one. Of what is left the newest (highest id) wins.
 */
export const pickCreatedPipeId = (
  list: HydraulicType[],
  args: { parentId: number; description: string; excludeIds?: ReadonlySet<number> },
): number | null => {
  let found: number | null = null
  const visit = (pipes: HydraulicType[]) => {
    pipes.forEach((pipe) => {
      if (
        String(pipe.description ?? '').trim() === args.description &&
        Number(pipe.parentId ?? 0) === args.parentId &&
        !args.excludeIds?.has(Number(pipe.id)) &&
        (found === null || pipe.id > found)
      ) {
        found = pipe.id
      }
      if (pipe.subPipes?.length) visit(pipe.subPipes)
    })
  }
  visit(list)
  return found
}

/**
 * Finds the pipe a create just made, for when the response did not name it,
 * or when the create failed in a way that may still have written it.
 *
 * Matched on parent plus description over a fresh list read (see
 * `pickCreatedPipeId`).
 */
export const findCreatedPipeId = async (args: {
  interfaceId: number
  parentId: number
  description: string
  excludeIds?: ReadonlySet<number>
}): Promise<number | null> => {
  const { data } = await hydraulicDataConfig.getAll({
    interfaceId: args.interfaceId,
    PageIndex: 1,
    PageSize: 0,
    Keyword: args.description,
  })
  return pickCreatedPipeId(data?.data ?? [], args)
}

/**
 * Parents before children: a draft sub-pipe can only be written once its draft
 * parent has a real id, and `POST /api/pipe` takes one record with one numeric
 * parentId — there is no inline-children payload to send them up together.
 *
 * Drafts are staged parent-first, so this is normally the order they are
 * already in; the pass is here so that stays true however the list was built.
 */
export const orderParentsFirst = (list: DraftPipe[]): DraftPipe[] => {
  const remaining = [...list]
  const ordered: DraftPipe[] = []
  const emitted = new Set<number>()

  while (remaining.length > 0) {
    const index = remaining.findIndex(
      (draft) => !isDraftId(draft.parentId) || emitted.has(draft.parentId),
    )
    // A draft whose draft parent is no longer staged can never be written; keep
    // it in the list (the save loop reports it) rather than looping forever.
    if (index === -1) {
      ordered.push(...remaining)
      break
    }
    const [next] = remaining.splice(index, 1)
    emitted.add(next.id)
    ordered.push(next)
  }

  return ordered
}
